import { RiskLevel } from 'constants/risk-level'
import { LeakType } from 'models/leak.model'
import { ReportState } from 'models/report.model'
import { ResponsePlanStatus } from 'models/response.plan.model'
import { ICyberSecurityTools } from 'models/score.model'
import { ScanType } from 'pages/companyV2/BulkScans/components/sections/BulkScanExpandedDetails'

interface IScoreHistory {
  score: number
  date: Date
  _id: string
}

interface IMonthlyScores {
  lastScore: number
  date: string
}

interface IOnboarding {
  registerId: string
  startedAt: Date
}

export interface ICompanyThreats {
  label: string
  quantity: number
  reported: number
  classification: 'strength' | 'critical' | 'important'
}

export interface ICompanyStats {
  seats: number
  trend: {
    status: 'up' | 'down' | 'neutral'
    points: number
  }
  seatsRemaining: number
  cyberScore: {
    high: number
    average: number
    low: number
  }
  participation: {
    usersScanned: number
    usersNotScanned: number
    rate: number
  }
  companyScans: number
  nextScanDate: Date
  lastScore: number | null
  threats: Array<ICompanyThreats>
  sumThreats: number
}
export interface ICompanyData {
  id: string
  name: string
  email: string[]
  seats: number
  companyDomains: string[]
  scoreHistory: IScoreHistory[]
  invite: string
  inviteCode: string
  country: string
  image: string
  active: boolean
  billingEmail: string
  createdBy: string
  updatedBy: string
  industry: string
  onboardingStatus: OnboardingStatus
  onboarding: IOnboarding
  monthlyScores?: IMonthlyScores[]
  scanIntervalInMonths: number | null
  nextScanOn: Date | null
  hasDeepScanModule: boolean
  contractEndDate: Date | null
  createdAt: Date
  updatedAt: Date
  isCyberMatchModuleEnabled?: boolean
}

export enum BatchScanStatus {
  ERROR = 'ERROR',
  INPROGRESS = 'INPROGRESS',
  COMPLETED = 'COMPLETED',
}
export interface IBatchScans {
  id: string
  fileName: string
  scanIds: string
  company: string
  issuer: string
  status: BatchScanStatus
  fileKey: string
  _id: string
  createdAt: string
  updatedAt: string
}

export enum ThreatLevel {
  LOW = 'LOW',
  MODERATE = 'MODERATE',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
  PENDING = 'PENDING',
}

export interface ICompanyDashboardData {
  scoreStatistics: {
    trend: number
    lastScanDate: Date | null
    nextScanDate: Date | null
    highest: number
    lowest: number
    average: number
  }
  userStatistics: {
    totalSeats: number
    totalUsers: number
    availableSeats: number
    scanned: number
    unscanned: number
    profileCompleted: number
  }
  reports: {
    [leakType in LeakType]: {
      registers: {
        reported: number
        exposed: number
      }
      total: number
      byState: { [state in ReportState]: number }
    }
  }
  cyberSecurityTools: {
    [key in keyof ICyberSecurityTools]: {
      registers: {
        exposed: number
      }
    }
  }
  responsePlan: { [key in ResponsePlanStatus]: number } & {
    total: number
    missing: number
  }
}

export interface IHighRiskDepartment {
  id: string
  name: string
  userCount: number
  users: {
    id: string
    name: string
    email: string
  }[]
  riskLevelByDepartment: {
    riskLevel: RiskLevel
    count: number
  }[]
}

export interface ICompanyDashboardDataV2 {
  scanInProgress: boolean
  scoreStatistics: {
    score: number
    users: number
    domains: number
    lastScanDate: Date
  } | null
  scanStatistics: {
    riskLevels: {
      [key in RiskLevel]: {
        count: number
        percentage: number
      }
    }
    highRiskDepartments: IHighRiskDepartment[]
    topVulnerabilities: { name: LeakType; count: number }[]
  }
  deepScanStatistics: {
    registers: number
    deepScanned: number
    exposed: number
    leaks: { [key in LeakType]: number }
    cyberSecurityTools: {
      [key in keyof ICyberSecurityTools]: number
    }
    pendingInvitesCount: number
  }
  lastUpdatedAt: string
  nextScanDate: Date
}

export interface ICompanyScoreTrend {
  trend: {
    score: number
    date: Date
  }[]
}

export type BatchScanUploadMetadataRecords = {
  uploaded: number
  valid: number
  scanned: number
  scanning: number
  invalid: number
  failed: number
  total: number
}

export enum BatchScanUploadMetadataStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SCANNING = 'scanning',
  COMPLETED = 'completed',
  FAILED = 'failed',
  UNAVAILABLE_SEATS = 'unavailable_seats',
  // Terminal status for batch scans created with triggerScan:false (e.g. "skip
  // scan, only update list") — registers were created but never scanned.
  CREATED_NO_SCAN = 'created_no_scan',
}

export interface ICompanyBatchScanData {
  id: string
  createdBy: string
  status: BatchScanUploadMetadataStatus
  records: BatchScanUploadMetadataRecords
  createdAt: Date
  updatedAt: Date
}

export enum OnboardingStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
}

export interface IOnboardingStatus {
  onboardingStatus: OnboardingStatus
}

export interface ICompanyAvailableSeatsOutputDto {
  totalSeats: number
  availableSeats: number
  usedSeats: number
  canUpload: boolean
}
export interface ICompanyAvailableSeatsInputDto {
  seats: number
  companyId: string
}

export interface ICreatedByUser {
  name: string
  email: string
}

export interface ICompanyBatchScanDataDetail {
  id: string
  companyId: string
  createdByUser: ICreatedByUser
  records: BatchScanUploadMetadataRecords
  status: string
  // Existing users whose CSV name changed and were re-scanned as a result.
  // Not part of `records` (which only tracks new-user creation progress).
  usersRenamed?: number
  fileKey: string
  filename?: string
  type: string
  domains: number
  createdAt: Date
  updatedAt: Date
  uploadedAt: Date
  scanStartedAt?: Date | null
  scanCompletedAt?: Date | null
  disableDownload?: boolean
}

export interface ICompanyProfileSettingsDataUpdate {
  industry: string
  companyDomains: string[]
  image: File | null | string
  userEmail: string
}

export interface IBulkScanRowData {
  id: string
  fileName: string
  fileKey: string
  uploadedBy: string
  status: string
}

export interface IBulkScansListParams {
  page?: number
  perPage?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  status?: string
  startDate?: string
  endDate?: string
  search?: string
}

export interface IBulkScansPaginatedResponse {
  data: ICompanyBatchScanDataDetail[]
  pagination: {
    currentPage: number
    totalRecords: number
    totalPages: number
    recordsPerPage: number
  }
  extras?: {
    totalActiveUsersCount: number
    latestBatchScan?: {
      date: string
      createdByUser: {
        name: string
      }
      type: ScanType
    }
  }
}
